/* eslint-disable react/prop-types */
import React from 'react';
import { get } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import adminApi from '../../services/admin-api';
import { exportCVMiUsers } from '../../utils/export';
import { MOVIE, SHOW } from '../../utils/contentType';

export const defaultSorts = [''];

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter }
}) {
  const count = preFilteredRows.length;

  const onChangeFilter = e => {
    setFilter(e.target.value || undefined);
  };

  const onClickFilter = e => {
    e.stopPropagation();
  };

  return (
    <input
      className="cvmi-admin__table-column-filter"
      value={filterValue || ''}
      onChange={onChangeFilter}
      onClick={onClickFilter}
      placeholder={`Search ${count} records...`}
    />
  );
}

const encodePermissions = () => {};

export const encodeAllowedCategories = (countries, date) => {
  let dateString;
  let countryString;
  let singleDateString;
  if (!date || date < 1) {
    dateString = 'date:*';
    singleDateString = 'singleDate:*';
  } else {
    dateString = `date:${date}`;
    singleDateString = `singleDate:${date}`;
  }

  if (!countries || (countries.length === 1 && countries[0] === 'Global')) {
    countryString = 'country:*';
  } else if (countries.length >= 1) {
    countryString = `country:${countries
      .filter(c => c !== 'Global')
      .join(',')}`;
  }

  const allowedDataCategories = [
    countryString,
    'age:*',
    'network:*',
    dateString,
    singleDateString,
    'genre:*',
    'gender:*',
    'show:*',
    'episode:*',
    'actor:*',
    'season:*',
    'metric:*',
    'runtime:*',
    'screencapId:*',
    'voteRank:*',
    'panel:*',
    'language:*',
    'movie:*',
    'first_release_date:*',
    'release_date:*',
    'studio:*',
    'future_release_date:*',
    'originating_country:*',
    'status:*',
    'parentComment:*',
    'titleId:*',
    'contentType:*',
    'title:*',
    'days_from_release:*',
    'platform:*'
  ];

  return allowedDataCategories;
};

export const decodeCountriesForFilter = lookerUserInfo => {
  const { allowedDataCategories } = lookerUserInfo;
  if (!allowedDataCategories) {
    return [];
  }

  const countryString = get(
    allowedDataCategories.filter(f => f.slice(0, 8) === 'country:'),
    0,
    ''
  ).split(':')[1];

  if (countryString === '*') {
    return [{ value: 'Global', label: 'Global' }];
  }

  if (countryString.length < 1) {
    return null;
  }

  return countryString.split(',').map(c => ({ value: c, label: c }));
};

export const encodePages = settings => {
  const pages = [];
  const dataCategories = [];
  const defaultPages = [
    '/profile',
    '/help',
    '/release-notes',
    '/search-results'
  ];
  const showPages = [
    '/',
    '/shows',
    '/show/*',
    '/shows/*',
    '/actors',
    '/actor/*',
    '/networks',
    '/network/*',
    '/report/*',
    '/reports',
    '/reports/*',
    '/report-builder',
    '/attribution',
    '/attribution/*'
  ];
  const moviePages = [
    '/',
    '/movie/*',
    '/movies',
    '/movies/*',
    '/actors',
    '/actor/*',
    '/networks',
    '/network/*',
    '/report/*',
    '/reports',
    '/reports/*',
    '/report-builder',
    '/attribution',
    '/attribution/*'
  ];

  const isMovies = settings.includes('Movies');
  const isShows = settings.includes('Shows');
  const isTitles = isMovies && isShows;

  if (isTitles) {
    pages.push(...showPages, ...moviePages, '/affinity');
    dataCategories.push('contentType:*');
  } else if (isMovies) {
    pages.push(...moviePages);
    dataCategories.push(`contentType:${MOVIE}`);
  } else if (isShows) {
    pages.push(...showPages);
    dataCategories.push(`contentType:${SHOW}`);
  }

  pages.push(...defaultPages);
  return {
    pages,
    dataCategories
  };
};

export const decodeDate = lookerUserInfo => {
  if (!lookerUserInfo) {
    return null;
  }
  const { allowedDataCategories } = lookerUserInfo;

  if (allowedDataCategories && allowedDataCategories.includes('date:*')) {
    return null;
  } else if (allowedDataCategories) {
    const datePermission = allowedDataCategories.filter(
      f => f.slice(0, 5) === 'date:'
    )[0];
    return datePermission.split(':')[1];
  }

  return null;
};

export const decodePages = lookerUserInfo => {
  if (!lookerUserInfo) {
    return [];
  }
  const { allowedPages } = lookerUserInfo;
  const string = [];

  if (allowedPages && allowedPages.includes('/shows')) {
    string.push('Shows');
  }

  if (allowedPages && allowedPages.includes('/movies')) {
    string.push('Movies');
  }

  return string;
};

export const decodeCountries = lookerUserInfo => {
  const { allowedDataCategories } = lookerUserInfo;
  const string = [];

  if (allowedDataCategories && allowedDataCategories.includes('country:*')) {
    string.push('Global');
  } else if (allowedDataCategories) {
    const country = allowedDataCategories.filter(
      f => f.slice(0, 8) === 'country:'
    )[0];
    const countries = country.split(':')[1];
    string.push(`${countries.split(',').join(', ')}`);
  }

  return string;
};

export const decodePermissions = lookerUserInfo => {
  const datePermission = decodeDate(lookerUserInfo);
  const string = [
    ...decodePages(lookerUserInfo),
    ...decodeCountries(lookerUserInfo)
  ];

  if (datePermission) {
    string.push(`${datePermission} Month Window`);
  }

  return string.join(', ');
};

const grantApiAccess = user => async () => {
  try {
    console.log('api access');
    const success = await adminApi.generateApiKey(user);

    return success;
  } catch (e) {}
};

const onClickGrantAPI = (user, alertActions) => () => {
  const {
    setShowAlert,
    setAlertText,
    setAlertFunc,
    setAlertTitle
  } = alertActions;
  setShowAlert(true);
  setAlertTitle('Grant API Key');
  setAlertText(
    'Please confirm you wish to grant an API Key to this user. This will give this user access to our external api. Only the user will have access to this key via their CVMi profile page.'
  );
  setAlertFunc(() => grantApiAccess(user));
};

const sendWelcomeEmail = user => async () => {
  try {
    const success = await adminApi.sendWelcomeEmail({
      ...user,
      lookerUserInfo: JSON.stringify(user.lookerUserInfo)
    });

    return success;
  } catch (e) {
    console.log(e);
  }
};

const onClickSendWelcome = (user, alertActions) => () => {
  const {
    setShowAlert,
    setAlertText,
    setAlertFunc,
    setAlertTitle
  } = alertActions;
  setShowAlert(true);
  setAlertTitle('Send Welcome Email');
  setAlertText(
    'Please confirm you wish to send this user a Welcome email. This will notify the recipient that a CVMi account has been created for them and provide them with instructions to set up their account and password.'
  );
  setAlertFunc(() => sendWelcomeEmail(user));
};

const onClickEdit = (user, onEditUser) => () => {
  onEditUser(user);
};

const formatName = row => {
  const firstName = get(row, ['original', 'username'], '');
  const lastName = get(row, ['original', 'lastname'], '');

  return (
    <div className="cvmi-admin__table-row-actions-wrapper">
      <div className="cvmi-admin__table-row-actions-name">{`${firstName} ${lastName}`}</div>
    </div>
  );
};

const formatLastLogin = timestamp => {
  let time = '';
  if (timestamp) {
    time = new Date(timestamp);
    return (
      <div className="cvmi-admin-user_table__admin-cell-last-login-wrapper">
        <div className="cvmi-admin-user_table__admin-cell-last-login-date">
          {time.toLocaleDateString()}
        </div>
        <div className="cvmi-admin-user_table__admin-cell-last-login-time">
          {time.toLocaleTimeString()}
        </div>
      </div>
    );
  }

  return null;
};

export const downloadTableState = (users, filters) => {
  const date = new Date().toLocaleDateString();
  const title = `CVMi_users_${date}${
    filters.length > 0 ? filters.map(({ id, value }) => `_${id}-${value}`) : ''
  }`;

  exportCVMiUsers(
    users.map(r => {
      const loginTimestamp = get(r, ['original', 'lastLogin'], null);
      const loginDate = loginTimestamp ? new Date(loginTimestamp) : false;
      if (r.values.email === 'amihalopoulos@whipmedia.com') {
        console.log(r);
      }
      return {
        Name: get(r, ['values', 'Name'], ''),
        Email: get(r, ['values', 'email'], ''),
        UserID: get(r, ['original', 'userId'], ''),
        Organization: get(r, ['values', 'organizationName'], ''),
        Permissions: [
          ...decodePages(get(r, ['values', 'lookerUserInfo'], '')),
          ...decodeCountries(get(r, ['values', 'lookerUserInfo'], ''))
        ].join(', '),
        'Last Login': loginDate
          ? `${loginDate.toLocaleDateString()} ${loginDate.toLocaleTimeString()}`
          : '',
        Status: get(r, ['values', 'disabled'], false) ? 'disabled' : 'active'
      };
    }),
    title,
    [
      'Name',
      'Email',
      'UserID',
      'Organization',
      'Permissions',
      'Last Login',
      'Status'
    ]
  );
};

export const createColumns = (onEditUser, alertActions) => [
  {
    Header: 'Name',
    accessor: row => `${row.username} ${row.lastname}`,
    className: 'cvmi-admin-user_table__admin-cell',
    filter: 'fuzzyText',
    Filter: DefaultColumnFilter,
    Cell: ({ row }) => formatName(row)
  },
  {
    Header: 'Email',
    accessor: 'email',
    className: 'cvmi-admin-user_table__admin-cell-large',
    filter: 'fuzzyText',
    Filter: DefaultColumnFilter
  },
  {
    Header: 'Organization',
    accessor: 'organizationName',
    sortDescFirst: false,
    className: 'cvmi-admin-user_table__admin-cell',
    filter: 'fuzzyText',
    Filter: DefaultColumnFilter
  },
  {
    Header: 'Permissions',
    accessor: 'lookerUserInfo',
    sortDescFirst: false,
    className: 'cvmi-admin-user_table__admin-cell-large',
    filter: 'fuzzyText',
    Filter: DefaultColumnFilter,
    disableFilters: true,
    Cell: ({ cell: { value } }) => decodePermissions(value)
  },
  {
    Header: 'Last Login',
    accessor: row => row.lastLogin || '',
    sortDescFirst: false,
    className: 'cvmi-admin-user_table__admin-cell-last-login',
    filter: 'fuzzyText',
    disableFilters: true,
    sortType: (rowA, rowB) =>
      (rowB.original.lastLogin || -1) - (rowA.original.lastLogin || -1),
    Filter: DefaultColumnFilter,
    Cell: ({ cell: { value } }) => formatLastLogin(value)
  },
  {
    Header: 'Actions',
    className: 'cvmi-admin-user_table__admin-cell-actions',
    filter: 'fuzzyText',
    Filter: DefaultColumnFilter,
    disableFilters: true,
    Cell: ({ cell: { value }, row }) => (
      <>
        <button
          className="cvmi-admin__send-welcome-email"
          onClick={onClickEdit(row.original, onEditUser)}
        >
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>Edit User</Tooltip>}
          >
            <FontAwesomeIcon icon="edit" size="1x" />
          </OverlayTrigger>
        </button>
        <button
          className="cvmi-admin__send-welcome-email"
          onClick={onClickSendWelcome(row.original, alertActions)}
        >
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>Send Welcome Email (New Users)</Tooltip>}
          >
            <FontAwesomeIcon icon="envelope" size="1x" />
          </OverlayTrigger>
        </button>
        <button
          className="cvmi-admin__send-welcome-email"
          onClick={onClickGrantAPI(row.original, alertActions)}
        >
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                Generate API Key for User. This key will only be visible on User
                Profile page
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon="database" size="1x" />
          </OverlayTrigger>
        </button>
      </>
    )
  },
  {
    Header: 'Status',
    accessor: 'disabled',
    className: 'cvmi-admin-user_table__admin-user-status',
    filter: 'fuzzyText',
    Filter: DefaultColumnFilter,
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ cell: { value }, row }) => (
      <div
        className={`cvmi-admin__disabled-status-${
          value ? 'disabled' : 'active'
        }`}
      />
    )
  }
];

export default {
  createColumns,
  defaultSorts,
  decodePermissions,
  encodePermissions,
  decodeCountries,
  decodePages,
  decodeDate,
  encodePages,
  encodeAllowedCategories,
  downloadTableState
};
